import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ListChecks, Mail, Lock, Loader2 } from 'lucide-react';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        (err?.message?.includes('Network')
          ? 'Ошибка сети. Проверьте подключение.'
          : err?.message?.includes('timeout')
          ? 'Сервер не отвечает. Попробуйте позже.'
          : 'Не удалось войти. Попробуйте ещё раз.');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-muted flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-lifted mb-5">
            <ListChecks className="w-7 h-7" strokeWidth={2.25} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Добро пожаловать</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            Нет аккаунта?{' '}
            <Link to="/register" className="font-medium text-primary-600 hover:text-primary-700">
              Создать
            </Link>
          </p>
        </div>

        <div className="card p-6 shadow-soft">
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="email" className="label">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-subtle pointer-events-none" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input pl-10"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">Пароль</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-subtle pointer-events-none" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input pl-10"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary btn-lg w-full">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Входим…
                </>
              ) : (
                'Войти'
              )}
            </button>

            <p className="text-center">
              <Link to="/forgot-password" className="text-sm text-ink-muted hover:text-ink">
                Забыли пароль?
              </Link>
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-ink-subtle">
          Защищено JWT • Ваши данные не передаются третьим лицам
        </p>
      </div>
    </div>
  );
};

export default Login;
